<template>
  <div class="hris-page">
    <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">Employee Management</span>
      <h1 class="hris-page-title">Incidents &amp; Sanctions</h1>
      <p class="hris-page-subtitle">Manage incident reports, notices to explain, and employee sanctions.</p>
    </div>

    <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
      <q-tabs
        v-model="mainTab"
        active-color="green"
        indicator-color="green"
        align="left"
        inline-label
        class="hris-sanctions-tabs"
        @input="onTabChange"
      >
        <q-tab name="incidents" label="Incident Reports" icon="fas fa-exclamation-triangle"></q-tab>
        <q-tab name="ntes" label="Notice to Explain" icon="fas fa-file-invoice"></q-tab>
        <q-tab name="sanctions" label="Sanctions" icon="fas fa-gavel"></q-tab>
      </q-tabs>
    </div>

    <q-tab-panels v-model="mainTab" animated style="background: transparent" class="q-pa-none">
      <q-tab-panel name="incidents" class="q-pa-none">
      <div class="row" v-if="mainTab === 'incidents'">
        <div class="col-12">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Create Incident Report"
              @click="openIncidentDialog"
            ></q-btn>
            <q-select
              v-model="incidentStatusFilter"
              :options="incidentStatusOptions"
              label="Filter Status"
              dense
              outlined
              color="green"
              emit-value
              map-options
              style="width: 200px"
              @input="getIncidentReports"
            ></q-select>
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="incidentFilter"
              placeholder="Search Incident Reports"
              dense
              outlined
              color="green"
              style="min-width: 220px;"
            ></q-input>
          </div>
        </div>

        <div class="col-12">
          <div class="hris-panel">
          <q-table
            flat
            :data="incidentReports"
            :columns="incidentColumns"
            row-key="id"
            :filter="incidentFilter"
            :loading="incidentLoading"
            separator="cell"
            class="my-sticky-column-table"
          >
            <template v-slot:loading>
              <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                <q-spinner color="green" size="40px"></q-spinner>
                <div>Loading incident reports...</div>
              </div>
            </template>
            <template v-slot:no-data>
              <div class="hris-empty full-width">No incident reports found.</div>
            </template>
            <template v-slot:body="props">
              <q-tr :props="props">
                <q-td key="employee_reported" :props="props">
                  <div>
                    {{
                      props.row.employee
                        ? props.row.employee.firstname +
                          " " +
                          props.row.employee.lastname
                        : "N/A"
                    }}
                  </div>
                  <div
                    v-if="props.row.reported_employee_id"
                    class="text-caption text-grey-7"
                  >
                    {{ props.row.reported_employee_id }}
                  </div>
                </q-td>
                <q-td key="incident_date" :props="props">{{
                  formatDate(props.row.incident_date)
                }}</q-td>
                <q-td
                  key="reason"
                  :props="props"
                  style="white-space: pre-wrap; max-width: 260px"
                  >{{ props.row.reason }}</q-td
                >
                <q-td key="status" :props="props">
                  <q-chip
                    :color="getIncidentStatusColor(props.row.status)"
                    text-color="white"
                    dense
                  >
                    {{ props.row.status }}
                  </q-chip>
                </q-td>
                <q-td key="issued_by" :props="props">
                  {{
                    props.row.issuer
                      ? props.row.issuer.firstname +
                        " " +
                        props.row.issuer.lastname
                      : "N/A"
                  }}
                </q-td>
                <q-td key="actions" :props="props">
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-eye"
                    @click="viewIncidentDetails(props.row)"
                  >
                    <q-tooltip>View Details</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-edit"
                    @click="editIncidentReport(props.row)"
                    v-if="props.row.status === 'active'"
                  >
                    <q-tooltip>Edit</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-file-invoice"
                    @click="issueNte(props.row)"
                    v-if="props.row.status === 'active'"
                  >
                    <q-tooltip>Issue NTE</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-check"
                    @click="confirmResolveIncident(props.row)"
                    v-if="props.row.status === 'active'"
                  >
                    <q-tooltip>Mark Resolved</q-tooltip>
                  </q-btn>
                </q-td>
              </q-tr>
            </template>
          </q-table>
          </div>
        </div>
      </div>
      </q-tab-panel>

      <q-tab-panel name="ntes" class="q-pa-none">
      <div class="row" v-if="mainTab === 'ntes'">
        <div class="col-12">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getNtes"
            ></q-btn>
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="nteFilter"
              placeholder="Search NTEs"
              dense
              outlined
              color="green"
              style="min-width: 220px;"
            ></q-input>
          </div>
        </div>

        <div class="col-12">
          <div class="hris-panel">
          <q-table
            flat
            :data="ntes"
            :columns="nteColumns"
            row-key="id"
            :filter="nteFilter"
            :loading="nteLoading"
            separator="cell"
            class="my-sticky-column-table"
          >
            <template v-slot:loading>
              <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                <q-spinner color="green" size="40px"></q-spinner>
                <div>Loading notices...</div>
              </div>
            </template>
            <template v-slot:no-data>
              <div class="hris-empty full-width">No notices to explain found.</div>
            </template>
            <template v-slot:body="props">
              <q-tr :props="props">
                <q-td key="employee" :props="props">
                  <div>
                    {{
                      props.row.employee
                        ? props.row.employee.firstname +
                          " " +
                          props.row.employee.lastname
                        : "N/A"
                    }}
                  </div>
                  <div
                    v-if="props.row.reported_employee_id"
                    class="text-caption text-grey-7"
                  >
                    {{ props.row.reported_employee_id }}
                  </div>
                </q-td>
                <q-td key="incident_date" :props="props">{{
                  formatDate(props.row.incident_report ? props.row.incident_report.incident_date : null)
                }}</q-td>
                <q-td key="deadline" :props="props">{{
                  formatDate(props.row.deadline)
                }}</q-td>
                <q-td key="status" :props="props">
                  <q-chip
                    :color="getNteStatusColor(props.row.status)"
                    text-color="white"
                    dense
                  >
                    {{ props.row.status }}
                  </q-chip>
                </q-td>
                <q-td key="issued_by" :props="props">
                  {{
                    props.row.issuer
                      ? props.row.issuer.firstname +
                        " " +
                        props.row.issuer.lastname
                      : "N/A"
                  }}
                </q-td>
                <q-td key="actions" :props="props">
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-eye"
                    @click="viewNteDetails(props.row)"
                  >
                    <q-tooltip>View Details</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-check"
                    @click="confirmResolveNte(props.row)"
                    v-if="['pending', 'submitted'].includes(props.row.status)"
                  >
                    <q-tooltip>Mark Resolved</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-gavel"
                    @click="escalateToSanction(props.row)"
                    v-if="['pending', 'submitted'].includes(props.row.status)"
                  >
                    <q-tooltip>Escalate to Sanction</q-tooltip>
                  </q-btn>
                </q-td>
              </q-tr>
            </template>
          </q-table>
          </div>
        </div>
      </div>
      </q-tab-panel>

      <q-tab-panel name="sanctions" class="q-pa-none">
      <div class="row" v-if="mainTab === 'sanctions'">
        <div class="col-12">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Create Sanction"
              @click="openCreateDialog"
            ></q-btn>
            <q-select
              v-model="statusFilter"
              :options="statusOptions"
              label="Filter Status"
              dense
              outlined
              color="green"
              emit-value
              map-options
              style="width: 200px"
              @input="getSanctions"
            ></q-select>
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="filter"
              placeholder="Search Sanctions"
              dense
              outlined
              color="green"
              style="min-width: 220px;"
            ></q-input>
          </div>
        </div>

        <div class="col-12">
          <div class="hris-panel">
          <q-table
            flat
            :data="sanctions"
            :columns="columns"
            row-key="id"
            :filter="filter"
            :loading="loading"
            separator="cell"
            class="my-sticky-column-table"
          >
            <template v-slot:loading>
              <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                <q-spinner color="green" size="40px"></q-spinner>
                <div>Loading sanctions...</div>
              </div>
            </template>
            <template v-slot:no-data>
              <div class="hris-empty full-width">No sanctions found.</div>
            </template>
            <template v-slot:body="props">
              <q-tr :props="props">
                <q-td key="employee" :props="props" :class="sanctionEmployeeCellHighlight(props.row) ? 'sanction-employee-year-warning' : null">
                  <div>
                    {{
                      props.row.employee
                        ? props.row.employee.firstname + " " + props.row.employee.lastname
                        : "N/A"
                    }}
                  </div>
                  <div v-if="props.row.employee && props.row.employee.employee_id" class="text-caption" :class="sanctionEmployeeCellHighlight(props.row) ? 'text-white' : 'text-grey-7'">
                    {{ props.row.employee.employee_id }}
                  </div>
                </q-td>
                <q-td key="sanction_type" :props="props">
                  <q-chip
                    :color="getSanctionColor(props.row.sanction_type)"
                    text-color="white"
                    dense
                  >
                    {{ props.row.sanction_type }}
                  </q-chip>
                </q-td>
                <q-td key="offense" :props="props">
                  {{ props.row.offense ? props.row.offense.name : 'N/A' }}
                </q-td>
                <q-td key="incident_date" :props="props">{{
                  formatDate(props.row.incident_date)
                }}</q-td>
                <q-td key="issued_date" :props="props">{{
                  formatDate(props.row.issued_date)
                }}</q-td>
                <q-td key="effective_date" :props="props">{{
                  formatDate(props.row.effective_date)
                }}</q-td>
                <q-td key="end_date" :props="props">{{
                  formatDate(props.row.end_date)
                }}</q-td>
                <q-td key="status" :props="props">
                  <q-chip
                    :color="props.row.status === 'active' ? 'green' : 'red'"
                    text-color="white"
                    dense
                  >
                    {{ props.row.status }}
                  </q-chip>
                </q-td>
                <q-td key="issued_by" :props="props">
                  {{
                    props.row.issuer
                      ? props.row.issuer.firstname +
                        " " +
                        props.row.issuer.lastname
                      : "N/A"
                  }}
                </q-td>
                <q-td key="actions" :props="props">
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-eye"
                    @click="viewDetails(props.row)"
                  >
                    <q-tooltip>View Details</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    class="hris-q-ghost"
                    icon="fas fa-edit"
                    @click="editSanction(props.row)"
                    v-if="props.row.status === 'active'"
                  >
                    <q-tooltip>Edit</q-tooltip>
                  </q-btn>
                  <q-btn
                    flat
                    round
                    dense
                    color="negative"
                    icon="fas fa-ban"
                    @click="confirmRevoke(props.row)"
                    v-if="props.row.status === 'active'"
                  >
                    <q-tooltip>Revoke</q-tooltip>
                  </q-btn>
                </q-td>
              </q-tr>
            </template>
          </q-table>
          </div>
        </div>
      </div>
      </q-tab-panel>
    </q-tab-panels>

    <!-- Issue NTE Dialog -->
    <q-dialog v-model="nteDialog" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Issue Notice to Explain</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost"></q-btn>
        </div>
        <div class="hris-modal-body">
          <q-form @submit="saveNte" class="q-gutter-md">
            <q-input
              v-model="nteForm.deadline"
              label="Deadline for Explanation"
              outlined
              dense
              color="green"
              mask="date"
              :rules="['date', (val) => !!val || 'Deadline is required']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateNte"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="nteForm.deadline"
                      @input="() => $refs.qDateNte.hide()"
                    ></q-date>
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>
            <q-input
              v-model="nteForm.admin_notes"
              label="Admin Notes / Instructions"
              type="textarea"
              outlined
              dense
              color="green"
            ></q-input>
            <div class="hris-form-actions" style="margin-top: 8px; padding-top: 16px;">
              <q-btn label="Cancel" unelevated class="hris-q-ghost" v-close-popup></q-btn>
              <q-btn
                label="Issue NTE"
                type="submit"
                unelevated
                class="hris-q-primary"
                :loading="nteSaving"
              ></q-btn>
            </div>
          </q-form>
        </div>
      </div>
    </q-dialog>

    <!-- NTE Details Dialog -->
    <q-dialog v-model="nteDetailsDialog">
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">NTE Details</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost"></q-btn>
        </div>
        <div class="hris-modal-body" v-if="selectedNte">
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Employee:</div>
            <div class="col-8">
              {{ selectedNte.employee ? selectedNte.employee.firstname + ' ' + selectedNte.employee.lastname : selectedNte.reported_employee_id }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedNte.incident_report">
            <div class="col-4 text-weight-bold">Incident Date:</div>
            <div class="col-8">{{ formatDate(selectedNte.incident_report.incident_date) }}</div>
          </div>
          <div class="row q-mb-sm" v-if="selectedNte.incident_report">
            <div class="col-4 text-weight-bold">Incident Reason:</div>
            <div class="col-8" style="white-space: pre-wrap">{{ selectedNte.incident_report.reason }}</div>
          </div>
          <q-separator class="q-my-md"></q-separator>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Deadline:</div>
            <div class="col-8">{{ formatDate(selectedNte.deadline) }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Status:</div>
            <div class="col-8">
              <q-chip :color="getNteStatusColor(selectedNte.status)" text-color="white" dense>
                {{ selectedNte.status }}
              </q-chip>
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Employee Explanation:</div>
            <div class="col-8" style="white-space: pre-wrap">{{ selectedNte.explanation || 'No explanation submitted yet.' }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Admin Notes:</div>
            <div class="col-8" style="white-space: pre-wrap">{{ selectedNte.admin_notes || 'N/A' }}</div>
          </div>
        </div>
      </div>
    </q-dialog>

    <!-- Resolve NTE Dialog -->
    <q-dialog v-model="nteResolveDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Mark NTE as Resolved</div>
        </q-card-section>
        <q-card-section>
          <q-form @submit="resolveNte" class="q-gutter-md">
            <q-input
              v-model="nteResolvedNotes"
              label="Resolution Notes"
              type="textarea"
              outlined
              :rules="[(val) => !!val || 'Resolution notes are required']"
            />
            <div class="row justify-end q-gutter-sm">
              <q-btn label="Cancel" color="grey" v-close-popup />
              <q-btn
                label="Confirm Resolved"
                type="submit"
                color="positive"
                :loading="nteResolving"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Create/Edit Sanction Dialog -->
    <q-dialog v-model="dialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ isEdit ? "Edit Sanction" : "New Sanction" }}
          </div>
          <q-space />
          <q-btn icon="fas fa-times" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <q-form @submit="saveSanction" class="q-gutter-md">
            <q-select
              v-if="!isEdit"
              v-model="selectedEmployee"
              use-input
              input-debounce="300"
              label="Search Employee"
              :options="employeeOptions"
              @filter="filterEmployees"
              option-label="name"
              option-value="id"
              emit-value
              map-options
              outlined
              :rules="[(val) => !!val || 'Employee is required']"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">
                    No results
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-input
              v-else
              label="Employee"
              :value="editEmployeeName"
              outlined
              readonly
            />
            <q-select
              v-model="form.sanction_type"
              :options="sanctionTypeOptions"
              label="Sanction Type"
              outlined
              :rules="[(val) => !!val || 'Sanction Type is required']"
              @input="onSanctionTypeChange"
            />
            <q-select
              v-model="form.offense_type"
              :options="offenseOptions"
              label="Offense"
              outlined
              emit-value
              map-options
              :rules="[(val) => !!val || 'Offense is required']"
            />

            <q-input
              v-model="form.incident_date"
              label="Incident Date"
              outlined
              mask="date"
              :rules="['date']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateProxy1"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form.incident_date"
                      @input="() => $refs.qDateProxy1.hide()"
                    />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>

            <q-input
              v-model="form.issued_date"
              label="Issued Date"
              outlined
              mask="date"
              :rules="['date']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateProxy2"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form.issued_date"
                      @input="() => $refs.qDateProxy2.hide()"
                    />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>

            <q-input
              v-if="requiresDateRange"
              v-model="form.effective_date"
              label="Effective Date"
              outlined
              mask="date"
              :rules="[(val) => !!val || 'Effective Date is required', 'date']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateProxy3"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form.effective_date"
                      @input="() => $refs.qDateProxy3.hide()"
                    />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>

            <q-input
              v-if="requiresDateRange"
              v-model="form.end_date"
              label="End Date"
              outlined
              mask="date"
              :rules="[(val) => !!val || 'End Date is required', 'date']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateProxy4"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form.end_date"
                      @input="() => $refs.qDateProxy4.hide()"
                    />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>

            <q-input
              v-model="form.reason"
              label="Reason/Description"
              type="textarea"
              outlined
              :rules="[(val) => !!val || 'Reason is required']"
            />

            <div class="row justify-end q-gutter-sm">
              <q-btn label="Cancel" color="grey" v-close-popup />
              <q-btn
                :label="isEdit ? 'Update' : 'Save'"
                type="submit"
                color="primary"
                :loading="saving"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Sanction Details Dialog -->
    <q-dialog v-model="detailsDialog">
      <q-card style="min-width: 400px">
        <q-card-section class="row items-center">
          <div class="text-h6">Sanction Details</div>
          <q-space />
          <q-btn icon="fas fa-times" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section v-if="selectedSanction" class="q-pt-none">
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Employee:</div>
            <div class="col-8">
              {{ selectedSanction.employee.firstname }}
              {{ selectedSanction.employee.lastname }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Type:</div>
            <div class="col-8">{{ selectedSanction.sanction_type }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Incident Date:</div>
            <div class="col-8">
              {{ formatDate(selectedSanction.incident_date) }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Issued Date:</div>
            <div class="col-8">
              {{ formatDate(selectedSanction.issued_date) }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedSanction.effective_date">
            <div class="col-4 text-weight-bold">Effective Date:</div>
            <div class="col-8">
              {{ formatDate(selectedSanction.effective_date) }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedSanction.end_date">
            <div class="col-4 text-weight-bold">End Date:</div>
            <div class="col-8">{{ formatDate(selectedSanction.end_date) }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Status:</div>
            <div class="col-8">
              <q-chip
                :color="selectedSanction.status === 'active' ? 'green' : 'red'"
                text-color="white"
                dense
              >
                {{ selectedSanction.status }}
              </q-chip>
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Reason:</div>
            <div class="col-8">{{ selectedSanction.reason }}</div>
          </div>
          <div class="row q-mb-sm" v-if="selectedSanction.status === 'revoked'">
            <div class="col-4 text-weight-bold">Revoked Reason:</div>
            <div class="col-8">{{ selectedSanction.revoked_reason }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Issued By:</div>
            <div class="col-8">
              {{
                selectedSanction.issuer
                  ? selectedSanction.issuer.firstname +
                    " " +
                    selectedSanction.issuer.lastname
                  : "N/A"
              }}
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Revoke Sanction Dialog -->
    <q-dialog v-model="revokeDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Revoke Sanction</div>
        </q-card-section>

        <q-card-section>
          <q-form @submit="revokeSanction" class="q-gutter-md">
            <q-input
              v-model="revokedReason"
              label="Reason for Revocation"
              type="textarea"
              outlined
              :rules="[(val) => !!val || 'Revocation reason is required']"
            />
            <div class="row justify-end q-gutter-sm">
              <q-btn label="Cancel" color="grey" v-close-popup />
              <q-btn
                label="Confirm Revoke"
                type="submit"
                color="negative"
                :loading="revoking"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Create / Edit Incident Report -->
    <q-dialog v-model="incidentDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{
              isIncidentEdit ? "Edit Incident Report" : "New Incident Report"
            }}
          </div>
          <q-space />
          <q-btn icon="fas fa-times" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section>
          <q-form @submit="saveIncidentReport" class="q-gutter-md">
            <q-select
              v-if="!isIncidentEdit"
              v-model="incidentReportedEmployee"
              use-input
              input-debounce="300"
              label="Employee reported (company ID)"
              :options="incidentEmployeeOptions"
              @filter="filterIncidentEmployees"
              option-label="name"
              option-value="employee_id"
              emit-value
              map-options
              outlined
              :rules="[(val) => !!val || 'Employee is required']"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">No results</q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-input
              v-else
              label="Employee reported"
              :value="incidentEditEmployeeName"
              outlined
              readonly
            />
            <q-input
              v-model="incidentForm.incident_date"
              label="Incident date"
              outlined
              mask="date"
              :rules="['date']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateIncident"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="incidentForm.incident_date"
                      @input="() => $refs.qDateIncident.hide()"
                    />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>
            <q-input
              v-model="incidentForm.reason"
              label="Reason / report"
              type="textarea"
              outlined
              :rules="[(val) => !!val || 'Reason is required']"
            />
            <div class="row justify-end q-gutter-sm">
              <q-btn label="Cancel" color="grey" v-close-popup />
              <q-btn
                :label="isIncidentEdit ? 'Update' : 'Save'"
                type="submit"
                color="primary"
                :loading="incidentSaving"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Incident details -->
    <q-dialog v-model="incidentDetailsDialog">
      <q-card style="min-width: 400px">
        <q-card-section class="row items-center">
          <div class="text-h6">Incident report details</div>
          <q-space />
          <q-btn icon="fas fa-times" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section v-if="selectedIncident" class="q-pt-none">
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Employee ID:</div>
            <div class="col-8">
              {{ selectedIncident.reported_employee_id }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedIncident.employee">
            <div class="col-4 text-weight-bold">Name:</div>
            <div class="col-8">
              {{ selectedIncident.employee.firstname }}
              {{ selectedIncident.employee.lastname }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Incident date:</div>
            <div class="col-8">
              {{ formatDate(selectedIncident.incident_date) }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Reason / report:</div>
            <div class="col-8" style="white-space: pre-wrap">
              {{ selectedIncident.reason }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Status:</div>
            <div class="col-8">
              <q-chip
                :color="getIncidentStatusColor(selectedIncident.status)"
                text-color="white"
                dense
              >
                {{ selectedIncident.status }}
              </q-chip>
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Issued by:</div>
            <div class="col-8">
              {{
                selectedIncident.issuer
                  ? selectedIncident.issuer.firstname +
                    " " +
                    selectedIncident.issuer.lastname
                  : "N/A"
              }}
            </div>
          </div>
          <div
            class="row q-mb-sm"
            v-if="selectedIncident.status === 'resolved'"
          >
            <div class="col-4 text-weight-bold">Resolution notes:</div>
            <div class="col-8" style="white-space: pre-wrap">
              {{ selectedIncident.resolved_reason }}
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Resolve incident -->
    <q-dialog v-model="incidentResolveDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Mark incident resolved</div>
        </q-card-section>
        <q-card-section>
          <q-form @submit="resolveIncidentReport" class="q-gutter-md">
            <q-input
              v-model="incidentResolvedReason"
              label="Resolution notes"
              type="textarea"
              outlined
              :rules="[(val) => !!val || 'Resolution notes are required']"
            />
            <div class="row justify-end q-gutter-sm">
              <q-btn label="Cancel" color="grey" v-close-popup />
              <q-btn
                label="Confirm resolved"
                type="submit"
                color="positive"
                :loading="incidentResolving"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      mainTab: "incidents",
      sanctions: [],
      loading: false,
      filter: "",
      statusFilter: "active",
      statusOptions: [
        { label: "All", value: "all" },
        { label: "Active", value: "active" },
        { label: "Revoked", value: "revoked" },
      ],
      columns: [
        {
          name: "employee",
          align: "left",
          label: "Employee",
          field: (row) =>
            row.employee
              ? row.employee.firstname + " " + row.employee.lastname
              : "",
          sortable: true,
        },
        {
          name: "sanction_type",
          align: "center",
          label: "Sanction Type",
          field: "sanction_type",
          sortable: true,
        },
        {
          name: "offense",
          align: "left",
          label: "Offense",
          field: (row) => (row.offense ? row.offense.name : "N/A"),
          sortable: true,
        },
        {
          name: "incident_date",
          align: "center",
          label: "Incident Date",
          field: "incident_date",
          sortable: true,
        },
        {
          name: "issued_date",
          align: "center",
          label: "Issued Date",
          field: "issued_date",
          sortable: true,
        },
        {
          name: "effective_date",
          align: "center",
          label: "Effective Date",
          field: "effective_date",
          sortable: true,
        },
        {
          name: "end_date",
          align: "center",
          label: "End Date",
          field: "end_date",
          sortable: true,
        },
        {
          name: "status",
          align: "center",
          label: "Status",
          field: "status",
          sortable: true,
        },
        {
          name: "issued_by",
          align: "left",
          label: "Issued By",
          field: (row) =>
            row.issuer ? row.issuer.firstname + " " + row.issuer.lastname : "",
          sortable: true,
        },
        { name: "actions", align: "center", label: "Actions" },
      ],
      dialog: false,
      isEdit: false,
      saving: false,
      selectedEmployee: null,
      employeeOptions: [],
      offenseOptions: [],
      sanctionTypeOptions: [
        "Verbal Warning",
        "Written Warning",
        "Suspension Without Pay",
        "Demotion",
        "Dismissal",
      ],
      sanctionTypesWithDateRange: [
        "Suspension Without Pay",
        "Demotion",
        "Dismissal",
      ],
      form: {
        id: null,
        employee_id: null,
        sanction_type: "",
        offense_type: "",
        reason: "",
        incident_date: "",
        issued_date: "",
        effective_date: "",
        end_date: "",
        nte_id: null,
        incident_report_id: null,
      },
      editEmployeeName: "",
      selectedSanction: null,
      detailsDialog: false,
      revokeDialog: false,
      revoking: false,
      revokedReason: "",
      revokeId: null,

      incidentReports: [],
      incidentLoading: false,
      incidentFilter: "",
      incidentStatusFilter: "active",
      incidentStatusOptions: [
        { label: "All", value: "all" },
        { label: "Active", value: "active" },
        { label: "Resolved", value: "resolved" },
      ],
      incidentColumns: [
        {
          name: "employee_reported",
          align: "left",
          label: "Employee reported",
          field: (row) => {
            const name = row.employee
              ? row.employee.firstname + " " + row.employee.lastname
              : "";
            const code = row.reported_employee_id || "";
            return (name + " " + code).trim();
          },
          sortable: true,
        },
        {
          name: "incident_date",
          align: "center",
          label: "Incident date",
          field: "incident_date",
          sortable: true,
        },
        {
          name: "reason",
          align: "left",
          label: "Reason / report",
          field: "reason",
          sortable: true,
        },
        {
          name: "status",
          align: "center",
          label: "Status",
          field: "status",
          sortable: true,
        },
        {
          name: "issued_by",
          align: "left",
          label: "Issued by",
          field: (row) =>
            row.issuer ? row.issuer.firstname + " " + row.issuer.lastname : "",
          sortable: true,
        },
        { name: "actions", align: "center", label: "Actions" },
      ],
      incidentDialog: false,
      isIncidentEdit: false,
      incidentSaving: false,
      incidentReportedEmployee: null,
      incidentEmployeeOptions: [],
      incidentEditEmployeeName: "",
      incidentForm: {
        id: null,
        incident_date: "",
        reason: "",
      },
      selectedIncident: null,
      incidentDetailsDialog: false,
      incidentResolveDialog: false,
      incidentResolving: false,
      incidentResolveId: null,
      incidentResolvedReason: "",

      ntes: [],
      nteLoading: false,
      nteFilter: "",
      nteColumns: [
        {
          name: "employee",
          align: "left",
          label: "Employee",
          field: (row) => {
            const name = row.employee
              ? row.employee.firstname + " " + row.employee.lastname
              : "";
            const code = row.reported_employee_id || "";
            return (name + " " + code).trim();
          },
          sortable: true,
        },
        {
          name: "incident_date",
          align: "center",
          label: "Incident Date",
          field: (row) => (row.incident_report ? row.incident_report.incident_date : "N/A"),
          sortable: true,
        },
        {
          name: "deadline",
          align: "center",
          label: "Deadline",
          field: "deadline",
          sortable: true,
        },
        {
          name: "status",
          align: "center",
          label: "Status",
          field: "status",
          sortable: true,
        },
        {
          name: "issued_by",
          align: "left",
          label: "Issued By",
          field: (row) =>
            row.issuer ? row.issuer.firstname + " " + row.issuer.lastname : "",
          sortable: true,
        },
        { name: "actions", align: "center", label: "Actions" },
      ],
      nteDialog: false,
      nteForm: {
        incident_report_id: null,
        deadline: "",
        admin_notes: "",
      },
      nteSaving: false,
      selectedNte: null,
      nteDetailsDialog: false,
      nteResolveDialog: false,
      nteResolving: false,
      nteResolveId: null,
      nteResolvedNotes: "",
    };
  },
  computed: {
    token() {
      return this.$store.getters["auth/getToken"];
    },
    requiresDateRange() {
      return this.sanctionTypesWithDateRange.includes(this.form.sanction_type);
    },
    /** Keys `employeeId_calendarYear` where that employee has ≥3 sanctions in that year (by incident_date, else issued_date). */
    sanctionEmployeeYearHotKeys() {
      const counts = {};
      for (const s of this.sanctions) {
        if (s == null || s.employee_id == null) continue;
        const ds = s.incident_date || s.issued_date;
        if (!ds) continue;
        const y = parseInt(String(ds).substring(0, 4), 10);
        if (Number.isNaN(y)) continue;
        const k = `${String(s.employee_id)}_${y}`;
        counts[k] = (counts[k] || 0) + 1;
      }
      const hot = {};
      Object.keys(counts).forEach((k) => {
        if (counts[k] >= 3) hot[k] = true;
      });
      return hot;
    },
  },
  mounted() {
    this.getSanctions();
    this.getIncidentReports();
    this.getNtes();
    this.loadOffenseTypes();
  },
  methods: {
    formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    onTabChange(val) {
      if (val === "sanctions") this.getSanctions();
      if (val === "incidents") this.getIncidentReports();
      if (val === "ntes") this.getNtes();
    },
    loadOffenseTypes() {
      axios.create({
          headers: { 'Authorization': `Bearer ${this.token}` }
      }).get(this.apiUrl + '/admin/setting/offense')
      .then((res) => {
        this.offenseOptions = res.data.data.map(item => ({
          label: item.name,
          value: item.id
        }));
      });
    },
    getSanctions() {
      this.loading = true;
      this.sanctions = [];
      let url = this.apiUrl + "/sanctions";
      if (this.statusFilter && this.statusFilter !== "all") {
        url += "?status=" + this.statusFilter;
      }

      axios
        .create({
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .get(url)
        .then((res) => {
          this.sanctions = res.data.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getIncidentReports() {
      this.incidentLoading = true;
      this.incidentReports = [];
      let url = this.apiUrl + "/incident-reports";
      if (this.incidentStatusFilter && this.incidentStatusFilter !== "all") {
        url += "?status=" + this.incidentStatusFilter;
      }
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(url)
        .then((res) => {
          this.incidentReports = res.data.data || [];
        })
        .finally(() => {
          this.incidentLoading = false;
        });
    },
    getIncidentStatusColor(status) {
      if (status === "active") return "green";
      if (status === "resolved") return "blue-grey";
      return "grey";
    },
    openCreateDialog() {
      this.isEdit = false;
      this.selectedEmployee = null;
      const today = new Date().toISOString().split("T")[0].replace(/-/g, "/");
      this.form = {
        employee_id: null,
        sanction_type: "",
        offense_type: "",
        reason: "",
        incident_date: today,
        issued_date: today,
        effective_date: "",
        end_date: "",
      };
      this.dialog = true;
    },
    openIncidentDialog() {
      this.isIncidentEdit = false;
      this.incidentReportedEmployee = null;
      const today = new Date().toISOString().split("T")[0].replace(/-/g, "/");
      this.incidentForm = {
        id: null,
        incident_date: today,
        reason: "",
      };
      this.incidentDialog = true;
    },
    onSanctionTypeChange(value) {
      if (!this.sanctionTypesWithDateRange.includes(value)) {
        this.form.effective_date = "";
        this.form.end_date = "";
      }
    },
    filterEmployees(val, update) {
      if (val === "") {
        axios
          .create({
            headers: { Authorization: `Bearer ${this.token}` },
          })
          .get(this.apiUrl + "/employees/search?keyword=")
          .then((res) => {
            update(() => {
              this.employeeOptions = res.data.data;
            });
          });
        return;
      }

      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(this.apiUrl + "/employees/search?keyword=" + val)
        .then((res) => {
          update(() => {
            this.employeeOptions = res.data.data;
          });
        })
        .catch((err) => {
          console.error("Error searching employees:", err);
        });
    },
    filterIncidentEmployees(val, update) {
      if (val === "") {
        axios
          .create({
            headers: { Authorization: `Bearer ${this.token}` },
          })
          .get(this.apiUrl + "/employees/search?keyword=")
          .then((res) => {
            update(() => {
              this.incidentEmployeeOptions = res.data.data;
            });
          });
        return;
      }
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(this.apiUrl + "/employees/search?keyword=" + val)
        .then((res) => {
          update(() => {
            this.incidentEmployeeOptions = res.data.data;
          });
        })
        .catch((err) => {
          console.error("Error searching employees:", err);
        });
    },
    saveSanction() {
      this.saving = true;
      const path = this.isEdit ? "/sanction/update" : "/sanction/create";

      const payload = { ...this.form };
      if (!this.isEdit) {
        payload.employee_id = this.selectedEmployee;
      }

      if (
        this.requiresDateRange &&
        payload.effective_date &&
        payload.end_date &&
        payload.end_date < payload.effective_date
      ) {
        this.$q.notify({
          color: "negative",
          message: "End Date must be on or after Effective Date",
          icon: "fas fa-exclamation-triangle",
        });
        this.saving = false;
        return;
      }

      if (payload.incident_date)
        payload.incident_date = payload.incident_date.replace(/\//g, "-");
      if (payload.issued_date)
        payload.issued_date = payload.issued_date.replace(/\//g, "-");
      if (payload.effective_date)
        payload.effective_date = payload.effective_date.replace(/\//g, "-");
      if (payload.end_date)
        payload.end_date = payload.end_date.replace(/\//g, "-");

      axios
        .create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .post(path, payload)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: res.data.message || "Sanction saved successfully",
              icon: "fas fa-check",
            });
            this.dialog = false;
            this.getSanctions();
          } else {
            this.$q.notify({
              color: "negative",
              message: res.data.message || "Failed to save sanction",
              icon: "fas fa-exclamation-triangle",
            });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    saveIncidentReport() {
      this.incidentSaving = true;
      const fd = new FormData();
      fd.append(
        "incident_date",
        this.incidentForm.incident_date.replace(/\//g, "-")
      );
      fd.append("reason", this.incidentForm.reason || "");
      let path = "/incident-report/create";
      if (this.isIncidentEdit) {
        path = "/incident-report/update";
        fd.append("id", String(this.incidentForm.id));
      } else {
        fd.append("employee_id", String(this.incidentReportedEmployee || ""));
      }
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post(path, fd)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: res.data.message || "Incident report saved",
              icon: "fas fa-check",
            });
            this.incidentDialog = false;
            this.getIncidentReports();
          } else {
            this.$q.notify({
              color: "negative",
              message: res.data.message || "Failed to save",
              icon: "fas fa-exclamation-triangle",
            });
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Request failed";
          this.$q.notify({
            color: "negative",
            message: msg,
            icon: "fas fa-exclamation-triangle",
          });
        })
        .finally(() => {
          this.incidentSaving = false;
        });
    },
    editSanction(row) {
      this.isEdit = true;
      this.editEmployeeName =
        row.employee.firstname + " " + row.employee.lastname;
      this.form = {
        id: row.id,
        employee_id: row.employee_id,
        sanction_type: row.sanction_type,
        reason: row.reason,
        incident_date: row.incident_date
          ? row.incident_date.replace(/-/g, "/")
          : "",
        issued_date: row.issued_date ? row.issued_date.replace(/-/g, "/") : "",
        effective_date: row.effective_date
          ? row.effective_date.replace(/-/g, "/")
          : "",
        end_date: row.end_date ? row.end_date.replace(/-/g, "/") : "",
      };
      this.dialog = true;
    },
    editIncidentReport(row) {
      this.isIncidentEdit = true;
      this.incidentEditEmployeeName = row.employee
        ? row.employee.firstname + " " + row.employee.lastname
        : row.reported_employee_id || "N/A";
      this.incidentForm = {
        id: row.id,
        incident_date: row.incident_date
          ? row.incident_date.replace(/-/g, "/")
          : "",
        reason: row.reason || "",
      };
      this.incidentDialog = true;
    },
    viewDetails(row) {
      this.selectedSanction = row;
      this.detailsDialog = true;
    },
    viewIncidentDetails(row) {
      this.selectedIncident = row;
      this.incidentDetailsDialog = true;
    },
    confirmRevoke(row) {
      this.revokeId = row.id;
      this.revokedReason = "";
      this.revokeDialog = true;
    },
    revokeSanction() {
      this.revoking = true;
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post(this.apiUrl + "/sanction/revoke", {
          id: this.revokeId,
          revoked_reason: this.revokedReason,
        })
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: "Sanction revoked successfully",
              icon: "fas fa-check",
            });
            this.revokeDialog = false;
            this.getSanctions();
          }
        })
        .finally(() => {
          this.revoking = false;
        });
    },
    confirmResolveIncident(row) {
      this.incidentResolveId = row.id;
      this.incidentResolvedReason = "";
      this.incidentResolveDialog = true;
    },
    resolveIncidentReport() {
      this.incidentResolving = true;
      const fd = new FormData();
      fd.append("id", String(this.incidentResolveId));
      fd.append("resolved_reason", this.incidentResolvedReason || "");
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/incident-report/resolve", fd)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: res.data.message || "Marked resolved",
              icon: "fas fa-check",
            });
            this.incidentResolveDialog = false;
            this.getIncidentReports();
          } else {
            this.$q.notify({
              color: "negative",
              message: res.data.message || "Failed to resolve",
              icon: "fas fa-exclamation-triangle",
            });
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Request failed";
          this.$q.notify({
            color: "negative",
            message: msg,
            icon: "fas fa-exclamation-triangle",
          });
        })
        .finally(() => {
          this.incidentResolving = false;
        });
    },
    getNtes() {
      this.nteLoading = true;
      this.ntes = [];
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(this.apiUrl + "/ntes")
        .then((res) => {
          this.ntes = res.data.data || [];
        })
        .finally(() => {
          this.nteLoading = false;
        });
    },
    getNteStatusColor(status) {
      switch (status) {
        case "pending":
          return "orange";
        case "submitted":
          return "blue";
        case "resolved":
          return "green";
        case "sanctioned":
          return "red";
        default:
          return "grey";
      }
    },
    issueNte(row) {
      const today = new Date();
      today.setDate(today.getDate() + 3); // Default deadline: 3 days from now
      const deadline = today.toISOString().split("T")[0].replace(/-/g, "/");
      this.nteForm = {
        incident_report_id: row.id,
        deadline: deadline,
        admin_notes: "",
      };
      this.nteDialog = true;
    },
    saveNte() {
      this.nteSaving = true;
      const payload = { ...this.nteForm };
      if (payload.deadline) payload.deadline = payload.deadline.replace(/\//g, "-");

      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/nte/create", payload)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: "Notice to Explain issued successfully",
              icon: "fas fa-check",
            });
            this.nteDialog = false;
            if (this.mainTab === "incidents") this.getIncidentReports();
            this.getNtes();
          }
        })
        .finally(() => {
          this.nteSaving = false;
        });
    },
    viewNteDetails(row) {
      this.selectedNte = row;
      this.nteDetailsDialog = true;
    },
    confirmResolveNte(row) {
      this.nteResolveId = row.id;
      this.nteResolvedNotes = "";
      this.nteResolveDialog = true;
    },
    resolveNte() {
      this.nteResolving = true;
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/nte/resolve", {
          id: this.nteResolveId,
          admin_notes: this.nteResolvedNotes,
        })
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: "NTE marked as resolved",
              icon: "fas fa-check",
            });
            this.nteResolveDialog = false;
            this.getNtes();
          }
        })
        .finally(() => {
          this.nteResolving = false;
        });
    },
    escalateToSanction(row) {
      this.isEdit = false;
      if (!row.employee || !row.employee.id) {
        this.$q.notify({
          color: "negative",
          message: "Employee data missing for this NTE.",
          icon: "fas fa-exclamation-triangle",
        });
        return;
      }

      // Ensure employee is in options so label shows
      const emp = {
        id: row.employee.id,
        employee_id: row.employee.employee_id,
        name: row.employee.firstname + " " + row.employee.lastname,
      };
      if (!this.employeeOptions.find((o) => o.id === emp.id)) {
        this.employeeOptions.push(emp);
      }

      this.selectedEmployee = row.employee.id; // Use employees.id
      this.editEmployeeName = emp.name;
      
      const today = new Date().toISOString().split("T")[0].replace(/-/g, "/");
      this.form = {
        id: null,
        employee_id: row.employee.id, // Use employees.id
        sanction_type: "",
        reason: row.incident_report ? row.incident_report.reason : "",
        incident_date: row.incident_report ? row.incident_report.incident_date.replace(/-/g, "/") : today,
        issued_date: today,
        effective_date: "",
        end_date: "",
        nte_id: row.id,
        incident_report_id: row.incident_report_id,
      };
      
      this.mainTab = "sanctions";
      this.dialog = true;
    },
    sanctionEmployeeCellHighlight(row) {
      if (!row || row.employee_id == null) return false;
      const ds = row.incident_date || row.issued_date;
      if (!ds) return false;
      const y = parseInt(String(ds).substring(0, 4), 10);
      if (Number.isNaN(y)) return false;
      const k = `${String(row.employee_id)}_${y}`;
      return !!this.sanctionEmployeeYearHotKeys[k];
    },
    getSanctionColor(type) {
      switch (type) {
        case "Verbal Warning":
          return "blue-grey";
        case "Written Warning":
          return "orange";
        case "Suspension Without Pay":
          return "red-8";
        case "Demotion":
          return "red-10";
        case "Dismissal":
          return "black";
        default:
          return "grey";
      }
    },
  },
};
</script>

<style scoped>
.sanction-employee-year-warning {
  background-color: #c62828 !important;
  color: #fff !important;
  font-weight: 600;
}
</style>
